import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <SEOHead
          title="Article Not Found – OOPBuy Spreadsheet"
          description="The article you are looking for does not exist. Browse our collection of OOPBuy Spreadsheet guides and product sourcing articles."
          noindex={true}
        />
        <Navbar />
        <div className="pt-24 pb-16">
          <div className="section-container text-center">
            <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="btn-primary text-sm inline-flex items-center gap-2">
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`OOPBuy Spreadsheet, ${article.category}, product sourcing, shopping guide, OOPBuy 2026`}
        canonicalUrl={`https://oopbuy.surf/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        publishedTime="2026-06-22"
        modifiedTime="2026-06-22"
      />

      <Navbar />

      <article className="pt-24 pb-16 md:pb-20">
        <div className="section-container">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-950 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium whitespace-nowrap">{article.category}</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 whitespace-nowrap">Article</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-800 text-xs font-semibold rounded-full tracking-wide uppercase mb-5">
                {article.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.excerpt}
              </p>
              <div className="mt-6 flex items-center gap-3 text-xs text-foreground-500">
                <span>Updated: June 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div className="prose-custom">
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              {article.sections.map((section, sIdx) => {
                if (section.type === 'heading' && section.heading) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                        {section.heading}
                      </h2>
                    </section>
                  );
                }
                if (section.type === 'text' && section.content) {
                  return (
                    <section key={sIdx} className="mb-6">
                      <p className="text-foreground-700 text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content }}></p>
                    </section>
                  );
                }
                if (section.type === 'list' && section.items) {
                  return (
                    <section key={sIdx} className="mb-8">
                      <ul className="space-y-3">
                        {section.items.map((item, iIdx) => (
                          <li key={iIdx} className="flex items-start gap-3 text-foreground-700 text-sm leading-relaxed">
                            <i className="ri-check-line text-accent-500 flex-shrink-0 mt-0.5"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                }
                if (section.type === 'table' && section.tableData) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <div className="overflow-x-auto rounded-xl border border-background-200/70">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-background-100">
                              <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-950 whitespace-nowrap">Feature</th>
                              <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-950 whitespace-nowrap">OOPBuy Spreadsheet</th>
                              <th className="text-left px-5 py-4 font-heading font-semibold text-foreground-950 whitespace-nowrap">Alternative</th>
                            </tr>
                          </thead>
                          <tbody>
                            {section.tableData.map((row, tIdx) => (
                              <tr key={tIdx} className={tIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                                <td className="px-5 py-3.5 font-medium text-foreground-950 whitespace-nowrap">{row.feature}</td>
                                <td className="px-5 py-3.5 text-accent-700">{row.oopbuy}</td>
                                <td className="px-5 py-3.5 text-foreground-600">{row.traditional}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  );
                }
                if (section.type === 'steps' && section.steps) {
                  return (
                    <section key={sIdx} className="mb-10">
                      <div className="space-y-5">
                        {section.steps.map((step, pIdx) => (
                          <div key={pIdx} className="flex gap-4 items-start">
                            <span className="text-4xl md:text-5xl font-heading font-bold text-accent-300 leading-none">{step.step}</span>
                            <div>
                              <h3 className="text-foreground-950 font-heading font-semibold text-lg">{step.title}</h3>
                              <p className="text-foreground-600 text-sm leading-relaxed mt-1">{step.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                }
                return null;
              })}

              {article.faqs.length > 0 && (
                <section className="mb-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {article.faqs.map((faq, fIdx) => (
                      <div key={fIdx} className="bg-background-100 rounded-xl p-5 md:p-6">
                        <h3 className="text-foreground-950 font-heading font-semibold text-base md:text-lg">{faq.q}</h3>
                        <p className="text-foreground-600 text-sm leading-relaxed mt-2">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="mb-10 bg-primary-50 rounded-2xl p-6 md:p-8 border border-primary-100">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Ready to Try the OOPBuy Spreadsheet?
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Now that you have read this guide, the next step is to experience the <strong>OOPBuy Spreadsheet</strong> for yourself. Head over to the main platform and start exploring the organized world of product sourcing today.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary text-sm inline-flex items-center gap-2 justify-center"
                  >
                    <i className="ri-shopping-bag-3-line"></i>
                    Explore OOPBuy Spreadsheet
                  </a>
                  <Link
                    to="/"
                    className="btn-outline text-sm inline-flex items-center gap-2 justify-center"
                  >
                    <i className="ri-home-4-line"></i>
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>

            <nav className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="flex items-center gap-2 text-foreground-600 hover:text-foreground-950 text-sm font-medium transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to OOPBuy Spreadsheet Home
                </Link>
                <div className="flex flex-wrap gap-3">
                  {articles.filter(a => a.slug !== slug).slice(0, 4).map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="text-xs text-foreground-500 hover:text-foreground-700 bg-background-100 px-3 py-1.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {a.category}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}